<template>
  <div class="rs-page">
    <div class="rs-content">
      <!-- KPI Cards: 7 columns -->
      <div class="rs-kpi-row">
        <div v-for="kpi in kpiCards" :key="kpi.label" class="rs-kpi-card">
          <div class="rs-kpi-header">
            <span v-if="kpi.iconColor" class="rs-kpi-icon" :style="{ color: kpi.iconColor }">
              <svg v-if="kpi.icon === 'shield'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <svg v-else-if="kpi.icon === 'dollar'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              <svg v-else-if="kpi.icon === 'clock'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <svg v-else-if="kpi.icon === 'bulb'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>
            </span>
            <span class="rs-kpi-label">{{ kpi.label }}</span>
          </div>
          <div class="rs-kpi-value">{{ kpi.value }}</div>
        </div>
      </div>

      <!-- Tabs -->
      <el-tabs v-model="activeTab" class="rs-tabs">
        <el-tab-pane label="实例总览" name="overview" />
        <el-tab-pane name="hierarchy">
          <template #label>层级视图</template>
        </el-tab-pane>
        <el-tab-pane label="覆盖率分析" name="coverage" />
        <el-tab-pane name="optimization">
          <template #label>
            成本优化
            <span class="rs-tab-badge rs-badge-orange">{{ optimizations.length }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane name="expiration">
          <template #label>
            到期管理
            <span v-if="expiringCount > 0" class="rs-tab-badge rs-badge-amber">{{ expiringCount }}</span>
          </template>
        </el-tab-pane>
      </el-tabs>

      <!-- Shared Filters -->
      <div class="rs-filters">
        <div class="rs-search-wrap">
          <svg class="rs-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input v-model="searchQuery" type="text" placeholder="搜索实例/Link..." class="rs-search-input" />
        </div>
        <el-select v-model="month" filterable size="small" class="rs-filter-select rs-filter-month" placeholder="选择月份" @change="onMonthChange">
          <el-option v-for="m in monthOptions" :key="m.value" :value="m.value" :label="m.label" />
        </el-select>
        <el-select v-if="!isLimited" v-model="partnerFilter" filterable size="small" class="rs-filter-select" placeholder="全部伙伴" @change="onPartnerChange">
          <el-option value="all" label="全部伙伴" />
          <el-option v-for="p in partners" :key="p.id" :value="p.name" :label="p.name" />
        </el-select>
        <el-select v-model="payerFilter" filterable size="small" class="rs-filter-select rs-filter-payer" placeholder="全部 Payer">
          <el-option value="all" label="全部 Payer" />
          <el-option v-for="pa in availablePayers" :key="pa.id" :value="pa.name" :label="pa.name" />
        </el-select>
        <el-select v-model="typeFilter" filterable size="small" class="rs-filter-select rs-filter-type" placeholder="全部类型">
          <el-option value="all" label="全部类型" />
          <el-option value="RI" label="RI" />
          <el-option value="SP" label="SP" />
        </el-select>
        <el-select v-model="statusFilter" filterable size="small" class="rs-filter-select rs-filter-status" placeholder="全部状态">
          <el-option value="all" label="全部状态" />
          <el-option value="active" label="活跃" />
          <el-option value="expiring-soon" label="即将到期" />
          <el-option value="expired" label="已过期" />
        </el-select>
      </div>

      <!-- Tab: Overview -->
      <div v-if="activeTab === 'overview'" class="rs-tab-body">
        <div class="rs-overview-grid">
          <div class="rs-chart-card rs-chart-main">
            <h3 class="rs-card-title">按 Payer 利用率与覆盖率</h3>
            <p class="rs-card-subtitle">各 Payer 的 RI/SP 平均利用率和覆盖率对比</p>
            <div ref="overviewBarChart" class="rs-chart" style="height:220px"></div>
          </div>
          <div class="rs-chart-side">
            <div class="rs-chart-card rs-pie-card">
              <h3 class="rs-card-title">类型分布</h3>
              <div class="rs-pie-row">
                <div ref="typePieChart" class="rs-pie-chart"></div>
                <div class="rs-pie-legend">
                  <div v-for="item in typeDistribution" :key="item.name" class="rs-legend-item">
                    <span class="rs-legend-dot" :style="{ backgroundColor: item.color }"></span>
                    <span class="rs-legend-label">{{ item.name }}</span>
                    <span class="rs-legend-value">{{ item.value }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="rs-chart-card rs-pie-card">
              <h3 class="rs-card-title">状态分布</h3>
              <div class="rs-pie-row">
                <div ref="statusPieChart" class="rs-pie-chart"></div>
                <div class="rs-pie-legend">
                  <div v-for="item in statusDistribution" :key="item.name" class="rs-legend-item">
                    <span class="rs-legend-dot" :style="{ backgroundColor: item.color }"></span>
                    <span class="rs-legend-label">{{ item.name }}</span>
                    <span class="rs-legend-value">{{ item.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Instance Table -->
        <div class="rs-table-card">
          <div class="rs-table-header">
            <h3 class="rs-card-title">全部实例 ({{ filteredInstances.length }})</h3>
          </div>
          <div class="rs-table-wrap">
            <table class="rs-table">
              <thead>
                <tr>
                  <th v-for="h in overviewHeaders" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="ri in pagedInstances" :key="ri.id">
                  <tr :class="{ 'rs-row-sp-expandable': ri.purchaseType === 'SP' }" @click="ri.purchaseType === 'SP' && toggleSpLink(ri)">
                    <td class="rs-td-id">
                      <span v-if="ri.purchaseType === 'SP'" class="rs-sp-caret">{{ expandedSp === ri.id ? '▾' : '▸' }}</span>
                      {{ ri.id }}
                    </td>
                    <td><span :class="['rs-type-badge', ri.purchaseType === 'RI' ? 'rs-badge-ri' : 'rs-badge-sp']">{{ ri.purchaseType }}</span></td>
                    <td class="rs-td-mono">{{ ri.instanceType }}</td>
                    <td class="rs-td-muted">{{ ri.platform }}</td>
                    <td class="rs-td-muted">{{ ri.region }}</td>
                    <td v-if="!isLimited" class="rs-td-muted">{{ (ri.partnerName || '').split(' ')[0] }}</td>
                    <td class="rs-td-muted">{{ ri.payerName }}</td>
                    <td class="rs-td-id">{{ ri.linkName }}</td>
                    <td>
                      <div class="rs-util-bar">
                        <div class="rs-util-track"><div class="rs-util-fill" :style="{ width: ri.utilization + '%', backgroundColor: utilColor(ri.utilization) }"></div></div>
                        <span class="rs-util-text">{{ ri.utilization }}%</span>
                      </div>
                    </td>
                    <td class="rs-td-mono rs-td-muted">{{ ri.coverage }}%</td>
                    <td class="rs-td-mono rs-td-green">${{ (ri.savings || 0).toLocaleString() }}</td>
                    <td class="rs-td-mono rs-td-muted">{{ ri.endDate }}</td>
                    <td><span :class="statusBadgeClass(ri.status)">{{ statusLabel(ri.status) }}</span></td>
                  </tr>
                  <tr v-if="ri.purchaseType === 'SP' && expandedSp === ri.id" class="rs-sp-detail-row">
                    <td :colspan="overviewHeaders.length">
                      <div class="rs-sp-detail">
                        <div v-if="spLoading" class="rs-sp-detail-msg">加载该 SP 的 Link 抵扣明细…</div>
                        <template v-else-if="spLinks.length">
                          <div class="rs-sp-detail-title">该 SP 在各 Link 的抵扣明细 · {{ formatMonth(month) }}（共 {{ spLinks.length }} 个 Link）</div>
                          <table class="rs-sp-detail-table">
                            <thead>
                              <tr>
                                <th>Link</th>
                                <th v-if="!isLimited">伙伴</th>
                                <th>覆盖按需成本</th>
                                <th>SP 有效成本</th>
                                <th>节省</th>
                                <th>覆盖服务</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="lk in spLinks" :key="lk.linkId">
                                <td class="rs-td-id">{{ lk.linkId }}</td>
                                <td v-if="!isLimited" class="rs-td-muted">{{ (lk.partnerName || '').split(' ')[0] }}</td>
                                <td class="rs-td-mono">${{ (lk.coveredCost || 0).toLocaleString() }}</td>
                                <td class="rs-td-mono">${{ (lk.effectiveCost || 0).toLocaleString() }}</td>
                                <td class="rs-td-mono rs-td-green">${{ (lk.savings || 0).toLocaleString() }}</td>
                                <td class="rs-td-muted rs-sp-detail-svc">{{ (lk.services || []).join('、') || '—' }}</td>
                              </tr>
                            </tbody>
                            <tfoot v-if="spTotal">
                              <tr>
                                <td class="rs-sp-detail-foot">合计</td>
                                <td v-if="!isLimited"></td>
                                <td class="rs-td-mono rs-sp-detail-foot">${{ (spTotal.coveredCost || 0).toLocaleString() }}</td>
                                <td class="rs-td-mono rs-sp-detail-foot">${{ (spTotal.effectiveCost || 0).toLocaleString() }}</td>
                                <td class="rs-td-mono rs-td-green rs-sp-detail-foot">${{ (spTotal.savings || 0).toLocaleString() }}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </template>
                        <div v-else class="rs-sp-detail-msg rs-sp-detail-empty">暂无该 SP 的 Link 抵扣数据（历史月份需回填后才有）</div>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="filteredInstances.length === 0"><td :colspan="overviewHeaders.length" class="rs-empty-cell">暂无匹配的实例</td></tr>
              </tbody>
            </table>
          </div>
          <div v-if="filteredInstances.length > instPageSize" class="rs-pagination">
            <el-pagination
              v-model:current-page="instPage"
              :page-size="instPageSize"
              :total="filteredInstances.length"
              layout="total, prev, pager, next"
              small background />
          </div>
        </div>
      </div>

      <!-- Tab: Hierarchy -->
      <div v-if="activeTab === 'hierarchy'" class="rs-tab-body">
        <div class="rs-table-card">
          <div class="rs-table-header">
            <h3 class="rs-card-title">层级视图 — Partner → Payer → Link</h3>
          </div>
          <div class="rs-hierarchy">
            <div v-for="partner in hierarchyData" :key="partner.id" class="rs-h-partner">
              <button class="rs-h-partner-btn" @click="togglePartner(partner.id)">
                <svg :class="['rs-h-chevron', { expanded: expandedPartner === partner.id }]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                <span class="rs-h-partner-name">{{ partner.name }}</span>
                <span class="rs-h-meta">{{ partner.totalInstances }} 个实例</span>
                <span class="rs-h-util">{{ partner.avgUtil }}% 利用率</span>
                <span class="rs-h-savings">${{ partner.totalSavings.toLocaleString() }}/mo</span>
              </button>
              <template v-if="expandedPartner === partner.id">
                <div v-for="payer in partner.payerGroups" :key="payer.id" class="rs-h-payer-wrap">
                  <button class="rs-h-payer-btn" @click="togglePayer(payer.id)">
                    <svg :class="['rs-h-chevron rs-h-chevron-sm', { expanded: expandedPayer === payer.id }]" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span class="rs-h-payer-name">{{ payer.name }}</span>
                    <span class="rs-h-meta">{{ payer.instances.length }} 个实例</span>
                    <span class="rs-h-util">{{ payer.avgUtil }}%</span>
                    <span class="rs-h-savings">${{ payer.totalSavings.toLocaleString() }}/mo</span>
                  </button>
                  <div v-if="expandedPayer === payer.id" class="rs-h-links">
                    <div v-for="ri in payer.instances" :key="ri.id" class="rs-h-link-row">
                      <span class="rs-h-link-id">{{ ri.id }}</span>
                      <span :class="['rs-type-badge', ri.purchaseType === 'RI' ? 'rs-badge-ri' : 'rs-badge-sp']">{{ ri.purchaseType }}</span>
                      <span class="rs-h-link-type">{{ ri.instanceType }}</span>
                      <span class="rs-h-link-name">{{ ri.linkName }}</span>
                      <span class="rs-h-link-util">{{ ri.utilization }}%</span>
                      <span class="rs-h-link-savings">${{ ri.savings.toLocaleString() }}/mo</span>
                      <span :class="statusBadgeClass(ri.status)">{{ statusLabel(ri.status) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Coverage -->
      <div v-if="activeTab === 'coverage'" class="rs-tab-body">
        <div class="rs-coverage-grid">
          <div class="rs-chart-card">
            <h3 class="rs-card-title">RI/SP 覆盖率趋势</h3>
            <p class="rs-card-subtitle">过去12个月覆盖率变化</p>
            <div ref="coverageAreaChart" class="rs-chart" style="height:240px"></div>
          </div>
          <div class="rs-chart-card">
            <h3 class="rs-card-title">按 Payer 覆盖率对比</h3>
            <p class="rs-card-subtitle">当前各 Payer 的 RI/SP 覆盖率</p>
            <div ref="coverageBarChart" class="rs-chart" style="height:240px"></div>
          </div>
        </div>
      </div>

      <!-- Tab: Optimization -->
      <div v-if="activeTab === 'optimization'" class="rs-tab-body">
        <div class="rs-opt-kpi-row">
          <div v-for="item in optKpis" :key="item.label" class="rs-opt-kpi-card">
            <div class="rs-opt-kpi-label">{{ item.label }}</div>
            <div :class="['rs-opt-kpi-value', item.colorClass]">{{ item.value }}</div>
          </div>
        </div>
        <div class="rs-opt-filters">
          <el-select v-model="optConfidence" filterable size="small" class="rs-filter-select rs-filter-type" placeholder="全部置信度">
            <el-option value="all" label="全部置信度" />
            <el-option value="high" label="高置信度" />
            <el-option value="medium" label="中置信度" />
            <el-option value="low" label="低置信度" />
          </el-select>
          <el-select v-model="optType" filterable size="small" class="rs-filter-select rs-filter-type" placeholder="全部类型">
            <el-option value="all" label="全部类型" />
            <el-option value="buy-ri" label="购买 RI" />
            <el-option value="buy-sp" label="购买 SP" />
            <el-option value="renew-ri" label="续期 RI" />
            <el-option value="rightsize" label="调整规格" />
            <el-option value="terminate" label="终止/不续期" />
          </el-select>
        </div>
        <div class="rs-opt-list">
          <div v-for="opt in filteredOptimizations" :key="opt.id" class="rs-opt-card">
            <div :class="['rs-opt-icon-wrap', optTypeClass(opt.type)]">
              <svg v-if="opt.type === 'buy-ri' || opt.type === 'buy-sp'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              <svg v-else-if="opt.type === 'renew-ri' || opt.type === 'renew-sp'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              <svg v-else-if="opt.type === 'rightsize'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </div>
            <div class="rs-opt-body">
              <div class="rs-opt-badges">
                <span :class="['rs-opt-type-badge', optTypeClass(opt.type)]">{{ optTypeLabel(opt.type) }}</span>
                <span :class="['rs-opt-conf-badge', optConfClass(opt.confidence)]">置信度: {{ optConfLabel(opt.confidence) }}</span>
              </div>
              <h4 class="rs-opt-title">{{ opt.title }}</h4>
              <p class="rs-opt-desc">{{ opt.description }}</p>
            </div>
            <div class="rs-opt-savings">
              <div class="rs-opt-savings-value">${{ (opt.estimatedSavings / 1000).toFixed(1) }}K</div>
              <div class="rs-opt-savings-label">年度节省</div>
            </div>
            <svg class="rs-opt-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </div>
        </div>
      </div>

      <!-- Tab: Expiration -->
      <div v-if="activeTab === 'expiration'" class="rs-tab-body">
        <div class="rs-table-card">
          <div class="rs-table-header rs-table-header-between">
            <h3 class="rs-card-title">到期管理</h3>
            <span class="rs-card-subtitle">{{ expiringInstances.length }} 个合约</span>
          </div>
          <div v-if="expiringInstances.length === 0" class="rs-empty-state">
            <p>暂无 RI/SP 合约数据</p>
          </div>
          <div v-else class="rs-table-wrap">
            <table class="rs-table">
              <thead>
                <tr>
                  <th v-for="h in expirationHeaders" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ri in pagedExpiringInstances" :key="ri.id" :class="{ 'rs-row-expired': ri.status === 'expired' }">
                  <td class="rs-td-id">{{ ri.id }}</td>
                  <td><span :class="['rs-type-badge', ri.purchaseType === 'RI' ? 'rs-badge-ri' : 'rs-badge-sp']">{{ ri.purchaseType }}</span></td>
                  <td class="rs-td-mono">{{ ri.instanceType }}</td>
                  <td v-if="!isLimited" class="rs-td-muted">{{ (ri.partnerName || '').split(' ')[0] }}</td>
                  <td class="rs-td-muted">{{ ri.payerName }}</td>
                  <td class="rs-td-id">{{ ri.linkName }}</td>
                  <td class="rs-td-mono">{{ ri.endDate }}</td>
                  <td>
                    <span :class="daysLeftClass(daysLeft(ri))">{{ daysLeftText(daysLeft(ri)) }}</span>
                  </td>
                  <td>
                    <div class="rs-util-bar">
                      <div class="rs-util-track"><div class="rs-util-fill" :style="{ width: ri.utilization + '%', backgroundColor: utilColor(ri.utilization) }"></div></div>
                      <span class="rs-util-text">{{ ri.utilization }}%</span>
                    </div>
                  </td>
                  <td class="rs-td-mono rs-td-green">${{ (ri.savings || 0).toLocaleString() }}</td>
                  <td><span :class="statusBadgeClass(ri.status)">{{ statusLabel(ri.status) }}</span></td>
                  <td>
                    <span v-if="ri.status === 'expiring-soon' && ri.utilization >= 80" class="rs-action-btn rs-action-renew">建议续期</span>
                    <span v-else-if="ri.status === 'expiring-soon' && ri.utilization < 80" class="rs-action-btn rs-action-evaluate">评估后续期</span>
                    <span v-else-if="ri.status === 'expired'" class="rs-action-btn rs-action-expired">已过期</span>
                    <span v-else class="rs-td-muted" style="font-size:11px">{{ daysLeft(ri) }}天后到期</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="expiringInstances.length > expPageSize" class="rs-pagination">
              <el-pagination
                v-model:current-page="expPage"
                :page-size="expPageSize"
                :total="expiringInstances.length"
                layout="total, prev, pager, next"
                small background />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { ajaxGet } from '@/api/request'

export default {
  name: 'RispSummary',
  data() {
    return {
      activeTab: 'overview',
      month: '',
      monthOptions: [],
      partnerFilter: 'all',
      payerFilter: 'all',
      typeFilter: 'all',
      statusFilter: 'all',
      searchQuery: '',
      expandedPartner: null,
      expandedPayer: null,
      optConfidence: 'all',
      optType: 'all',
      partners: [],
      allPayers: [],
      riInstances: [],
      coverageTrend: [],
      optimizations: [],
      partnerCompare: [],
      optKpiData: { totalSavings: 0, highConfidenceSavings: 0, count: 0, affectedInstances: 0 },
      kpiData: { riCount: 0, spCount: 0, avgUtilization: 0, totalSavings: 0, expiringCount: 0 },
      chartInstances: [],
      instPage: 1,
      instPageSize: 15,
      expPage: 1,
      expPageSize: 15,
      // SP×link 抵扣明细展开（一次只展开一个 SP）
      expandedSp: null,
      spLinks: [],
      spTotal: null,
      spLoading: false,
      spCache: {},
    }
  },

  computed: {
    isLimited() {
      const ui = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const t = Number(ui.type) || 0
      return t === 2 || t === 4
    },
    overviewHeaders() {
      const h = ['ID', '类型', '实例类型', '平台', '区域', '伙伴', 'Payer', 'Link', '利用率', '覆盖率', '月度节省', '到期日', '状态']
      return this.isLimited ? h.filter(x => x !== '伙伴') : h
    },
    expirationHeaders() {
      const h = ['ID', '类型', '实例类型', '伙伴', 'Payer', 'Link', '到期日', '剩余天数', '利用率', '月度节省', '状态', '建议操作']
      return this.isLimited ? h.filter(x => x !== '伙伴') : h
    },
    totalRI() { return this.kpiData.riCount },
    totalSP() { return this.kpiData.spCount },
    avgUtilization() {
      // Weighted by contract count across the (filtered) payers so the KPI card
      // responds to the partner/payer filter.
      const data = this.rawFilteredPayerCompare
      if (data.length === 0) return this.kpiData.avgUtilization
      let wsum = 0, w = 0
      data.forEach(p => {
        const c = p.contractCount || 0
        if (c > 0) { wsum += (p.avgUtilization || 0) * c; w += c }
      })
      return w > 0 ? Math.round(wsum / w * 10) / 10 : this.kpiData.avgUtilization
    },
    avgCoverage() {
      // Weighted by each payer's coverable spend (totalSpend) so the综合覆盖率 KPI
      // reflects the current partner/payer filter, matching the AWS console figure.
      const data = this.rawFilteredPayerCompare
      if (data.length === 0) {
        if (this.coverageTrend.length === 0) return 0
        return this.coverageTrend[this.coverageTrend.length - 1].totalCoverage
      }
      let wsum = 0, w = 0
      data.forEach(p => {
        const s = p.totalSpend || 0
        if (s > 0) { wsum += (p.avgCoverage || 0) * s; w += s }
      })
      return w > 0 ? Math.round(wsum / w * 10) / 10 : 0
    },
    totalSavings() { return this.kpiData.totalSavings },
    expiringCount() { return this.kpiData.expiringCount },
    potentialSavings() { return this.optKpiData.totalSavings },
    kpiCards() {
      return [
        { label: 'RI 数量', value: String(this.totalRI), icon: 'shield', iconColor: '#3b82f6' },
        { label: 'SP 数量', value: String(this.totalSP), icon: 'shield', iconColor: '#8b5cf6' },
        { label: '平均利用率', value: this.avgUtilization + '%', icon: null, iconColor: null },
        { label: '综合覆盖率', value: this.avgCoverage + '%', icon: null, iconColor: null },
        { label: '月度节省', value: '$' + (this.totalSavings / 1000).toFixed(1) + 'K', icon: 'dollar', iconColor: '#10b981' },
        { label: '即将到期', value: String(this.expiringCount), icon: 'clock', iconColor: '#f59e0b' },
        { label: '可优化节省', value: '$' + (this.potentialSavings / 1000).toFixed(0) + 'K/yr', icon: 'bulb', iconColor: '#f97316' },
      ]
    },
    typeDistribution() {
      return [
        { name: 'Reserved Instances', value: this.totalRI, color: '#4a7fc1' },
        { name: 'Savings Plans', value: this.totalSP, color: '#7b68b8' },
      ]
    },
    statusDistribution() {
      const active = this.riInstances.filter(r => r.status === 'active').length
      const expiring = this.riInstances.filter(r => r.status === 'expiring-soon').length
      const expired = this.riInstances.filter(r => r.status === 'expired').length
      return [
        { name: '活跃', value: active, color: '#3d9e7a' },
        { name: '即将到期', value: expiring, color: '#c4894a' },
        { name: '已过期', value: expired, color: '#c05c5c' },
      ]
    },
    utilizationByPartner() {
      return this.partnerCompare.map(p => ({
        name: p.name || p.payerId || '',
        utilization: p.avgUtilization || 0,
        coverage: p.avgCoverage || 0,
      }))
    },
    filteredPayerCompare() {
      // Filter partnerCompare (payer-level data) by selected partner/payer
      let data = this.partnerCompare
      if (this.partnerFilter !== 'all') {
        // Find payer_ids belonging to this partner from instances
        const partnerPayerIds = new Set()
        this.riInstances.filter(i => i.partnerName === this.partnerFilter).forEach(i => {
          if (i.payerId) partnerPayerIds.add(i.payerId)
        })
        data = data.filter(p => partnerPayerIds.has(p.payerId))
      }
      if (this.payerFilter !== 'all') {
        data = data.filter(p => p.name === this.payerFilter || p.payerId === this.payerFilter)
      }
      return data.map(p => ({
        name: p.name || p.payerId || '',
        riCoverage: p.avgRiCoverage || 0,
        spCoverage: p.avgSpCoverage || 0,
        avgCoverage: p.avgCoverage || 0,
        avgUtilization: p.avgUtilization || 0,
      }))
    },
    rawFilteredPayerCompare() {
      // Same partner/payer filtering as filteredPayerCompare, but keeps the raw
      // totalSpend / contractCount fields needed for KPI weighting.
      let data = this.partnerCompare
      if (this.partnerFilter !== 'all') {
        const ids = new Set()
        this.riInstances.filter(i => i.partnerName === this.partnerFilter).forEach(i => {
          if (i.payerId) ids.add(i.payerId)
        })
        data = data.filter(p => ids.has(p.payerId))
      }
      if (this.payerFilter !== 'all') {
        data = data.filter(p => p.name === this.payerFilter || p.payerId === this.payerFilter)
      }
      return data
    },
    availablePayers() {
      if (this.partnerFilter === 'all') return this.allPayers
      return this.allPayers.filter(p => p.partnerName === this.partnerFilter)
    },
    filteredInstances() {
      return this.riInstances.filter(ri => {
        if (this.partnerFilter !== 'all' && ri.partnerName !== this.partnerFilter) return false
        if (this.payerFilter !== 'all' && ri.payerName !== this.payerFilter) return false
        if (this.typeFilter !== 'all' && ri.purchaseType !== this.typeFilter) return false
        if (this.statusFilter !== 'all' && ri.status !== this.statusFilter) return false
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          if (!(ri.instanceType || '').toLowerCase().includes(q) && !(ri.linkName || '').toLowerCase().includes(q) && !(ri.payerName || '').toLowerCase().includes(q)) return false
        }
        return true
      }).sort((a, b) => (b.utilization || 0) - (a.utilization || 0))
    },
    pagedInstances() {
      const start = (this.instPage - 1) * this.instPageSize
      return this.filteredInstances.slice(start, start + this.instPageSize)
    },
    filteredOptimizations() {
      // 共享筛选：伙伴（通过 payerName → partnerName 映射）/ Payer / 搜索
      let partnerPayerNames = null
      if (this.partnerFilter !== 'all') {
        partnerPayerNames = new Set(
          this.riInstances.filter(i => i.partnerName === this.partnerFilter).map(i => i.payerName)
        )
      }
      const q = this.searchQuery ? this.searchQuery.toLowerCase() : ''
      return this.optimizations.filter(o => {
        if (this.optConfidence !== 'all' && o.confidence !== this.optConfidence) return false
        if (this.optType !== 'all' && o.type !== this.optType) return false
        if (partnerPayerNames && !partnerPayerNames.has(o.payerName)) return false
        if (this.payerFilter !== 'all' && o.payerName !== this.payerFilter) return false
        if (q && !((o.title || '').toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q)
            || (o.linkName || '').toLowerCase().includes(q) || (o.payerName || '').toLowerCase().includes(q))) return false
        return true
      })
    },
    hierarchyData() {
      const partnerMap = {}
      for (const inst of this.filteredInstances) {
        const pn = inst.partnerName || '未分配'
        if (!partnerMap[pn]) partnerMap[pn] = {}
        const payerKey = inst.payerName || inst.payerId || '未知'
        if (!partnerMap[pn][payerKey]) partnerMap[pn][payerKey] = []
        partnerMap[pn][payerKey].push(inst)
      }
      return Object.entries(partnerMap).map(([pName, payers]) => {
        const allInst = Object.values(payers).flat()
        return {
          id: pName, name: pName,
          payerGroups: Object.entries(payers).map(([payerName, instances]) => ({
            id: payerName, name: payerName, instances,
            avgUtil: instances.length > 0 ? Math.round(instances.reduce((s, r) => s + r.utilization, 0) / instances.length) : 0,
            totalSavings: instances.reduce((s, r) => s + r.savings, 0),
          })),
          totalInstances: allInst.length,
          avgUtil: allInst.length > 0 ? Math.round(allInst.reduce((s, r) => s + r.utilization, 0) / allInst.length) : 0,
          totalSavings: allInst.reduce((s, r) => s + r.savings, 0),
        }
      }).filter(p => p.totalInstances > 0)
    },
    expiringInstances() {
      // 套用共享筛选（伙伴/Payer/类型/状态/搜索）后，按到期日升序
      return this.riInstances.filter(ri => {
        if (!ri.endDate) return false
        if (this.partnerFilter !== 'all' && ri.partnerName !== this.partnerFilter) return false
        if (this.payerFilter !== 'all' && ri.payerName !== this.payerFilter) return false
        if (this.typeFilter !== 'all' && ri.purchaseType !== this.typeFilter) return false
        if (this.statusFilter !== 'all' && ri.status !== this.statusFilter) return false
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          if (!(ri.instanceType || '').toLowerCase().includes(q) && !(ri.linkName || '').toLowerCase().includes(q) && !(ri.payerName || '').toLowerCase().includes(q)) return false
        }
        return true
      }).sort((a, b) => (a.endDate || '').localeCompare(b.endDate || ''))
    },
    pagedExpiringInstances() {
      const start = (this.expPage - 1) * this.expPageSize
      return this.expiringInstances.slice(start, start + this.expPageSize)
    },
    optKpis() {
      return [
        { label: '年度可节省总额', value: '$' + (this.optKpiData.totalSavings / 1000).toFixed(0) + 'K', colorClass: 'rs-color-emerald' },
        { label: '高置信度节省', value: '$' + (this.optKpiData.highConfidenceSavings / 1000).toFixed(0) + 'K', colorClass: 'rs-color-blue' },
        { label: '优化建议数', value: String(this.optKpiData.count), colorClass: '' },
        { label: '涉及实例数', value: String(this.optKpiData.affectedInstances), colorClass: '' },
      ]
    },
  },

  watch: {
    activeTab(val) {
      this.$nextTick(() => {
        if (val === 'overview') this.initOverviewCharts()
        else if (val === 'coverage') this.initCoverageCharts()
      })
    },
    partnerFilter() {
      this.instPage = 1
      this.expPage = 1
      if (this.activeTab === 'overview') this.$nextTick(() => this.initOverviewCharts())
      else if (this.activeTab === 'coverage') this.$nextTick(() => this.initCoverageCharts())
    },
    payerFilter() {
      this.instPage = 1
      this.expPage = 1
      if (this.activeTab === 'overview') this.$nextTick(() => this.initOverviewCharts())
      else if (this.activeTab === 'coverage') this.$nextTick(() => this.initCoverageCharts())
    },
    typeFilter() { this.instPage = 1; this.expPage = 1 },
    statusFilter() { this.instPage = 1; this.expPage = 1 },
    searchQuery() { this.instPage = 1; this.expPage = 1 },
  },
  mounted() {
    this.loadAllData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.chartInstances.forEach(c => c && !c.isDisposed() && c.dispose())
    this.chartInstances = []
  },
  methods: {
    async loadAllData() {
      const q = this.month ? { month: this.month } : {}
      const [instRes, trendRes, partnerRes, optRes] = await Promise.all([
        ajaxGet({ url: '/stat/risp/instances/', params: q }).catch(() => null),
        ajaxGet({ url: '/stat/risp/coverage_trend/' }).catch(() => null),
        ajaxGet({ url: '/stat/risp/partner_compare/', params: q }).catch(() => null),
        ajaxGet({ url: '/stat/risp/optimizations/', params: q }).catch(() => null),
      ])
      if (instRes && instRes.data) {
        this.riInstances = instRes.data.instances || []
        this.kpiData = instRes.data.kpi || this.kpiData
        this.partners = instRes.data.partners || []
        this.allPayers = instRes.data.payers || []
        this.monthOptions = instRes.data.months || []
        // On first load, adopt the backend's default month (latest COMPLETE month).
        if (!this.month && instRes.data.month) this.month = instRes.data.month
      }
      if (trendRes && trendRes.data) {
        this.coverageTrend = trendRes.data.trend || []
      }
      if (partnerRes && partnerRes.data) {
        this.partnerCompare = partnerRes.data.payers || []
      }
      if (optRes && optRes.data) {
        this.optimizations = optRes.data.optimizations || []
        this.optKpiData = optRes.data.kpi || this.optKpiData
      }
      this.$nextTick(() => this.initOverviewCharts())
    },
    onMonthChange() {
      // Re-fetch all month-scoped data for the newly selected month.
      this.instPage = 1
      this.expPage = 1
      // 月份变了，SP×link 明细缓存与展开态作废
      this.expandedSp = null
      this.spLinks = []
      this.spTotal = null
      this.spCache = {}
      this.loadAllData()
    },
    formatMonth(m) {
      const n = Number(m)
      if (!n) return ''
      return Math.floor(n / 100) + '-' + String(n % 100).padStart(2, '0')
    },
    async toggleSpLink(ri) {
      // 点击 SP 行展开/收起它在各 Link 的抵扣明细
      if (this.expandedSp === ri.id) { this.expandedSp = null; return }
      this.expandedSp = ri.id
      const key = ri.id + '|' + this.month
      const cached = this.spCache[key]
      if (cached) {
        this.spLinks = cached.links
        this.spTotal = cached.total
        this.spLoading = false
        return
      }
      this.spLoading = true
      this.spLinks = []
      this.spTotal = null
      try {
        const res = await ajaxGet({
          url: '/stat/risp/sp_link_breakdown/',
          params: { month: this.month, sp_arn: ri.id },
        })
        if (res && res.data) {
          this.spLinks = res.data.links || []
          this.spTotal = res.data.total || null
          this.spCache = { ...this.spCache, [key]: { links: this.spLinks, total: this.spTotal } }
        }
      } catch (e) {
        this.spLinks = []
        this.spTotal = null
      }
      this.spLoading = false
    },
    handleResize() { this.chartInstances.forEach(c => c && !c.isDisposed() && c.resize()) },
    initChart(ref) {
      const el = this.$refs[ref]
      if (!el) return null
      let inst = echarts.getInstanceByDom(el)
      if (inst) inst.dispose()
      inst = echarts.init(el)
      this.chartInstances.push(inst)
      return inst
    },

    initOverviewCharts() {
      // Bar chart: utilization vs coverage by payer (filtered)
      const bar = this.initChart('overviewBarChart')
      if (bar) {
        const data = this.filteredPayerCompare
        if (data.length === 0) {
          bar.setOption({ title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { fontSize: 13, color: '#999' } } })
        } else {
          bar.setOption({
            tooltip: { trigger: 'axis', textStyle: { fontSize: 12 }, borderRadius: 6, borderColor: '#e5e7eb' },
            legend: { bottom: 0, textStyle: { fontSize: 11, color: '#6b7280' }, icon: 'rect', itemWidth: 8, itemHeight: 8 },
            grid: { top: 10, right: 20, bottom: 36, left: 40 },
            xAxis: { type: 'category', data: data.map(d => d.name), axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false }, axisLabel: { fontSize: 11, color: '#6b7280' } },
            yAxis: { type: 'value', min: 0, max: 100, axisLabel: { fontSize: 11, color: '#6b7280', formatter: '{value}%' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } },
            series: [
              { name: '利用率', type: 'bar', data: data.map(d => d.avgUtilization), itemStyle: { color: '#4a7fc1', borderRadius: [3, 3, 0, 0] }, barGap: '30%' },
              { name: '覆盖率', type: 'bar', data: data.map(d => d.avgCoverage), itemStyle: { color: '#7b68b8', borderRadius: [3, 3, 0, 0] } },
            ],
          })
        }
      }
      // Type pie
      const typePie = this.initChart('typePieChart')
      if (typePie) {
        typePie.setOption({
          series: [{
            type: 'pie', radius: ['55%', '90%'], center: ['50%', '50%'], padAngle: 3, silent: false,
            label: { show: false }, emphasis: { scale: false },
            data: this.typeDistribution.map(d => ({ value: d.value, name: d.name, itemStyle: { color: d.color } })),
          }],
        })
      }
      // Status pie
      const statusPie = this.initChart('statusPieChart')
      if (statusPie) {
        statusPie.setOption({
          series: [{
            type: 'pie', radius: ['55%', '90%'], center: ['50%', '50%'], padAngle: 3, silent: false,
            label: { show: false }, emphasis: { scale: false },
            data: this.statusDistribution.map(d => ({ value: d.value, name: d.name, itemStyle: { color: d.color } })),
          }],
        })
      }
    },

    initCoverageCharts() {
      // Area chart: coverage trend (RI + SP only, no total)
      const area = this.initChart('coverageAreaChart')
      if (area) {
        const trendData = this.coverageTrend
        if (trendData.length === 0) {
          area.setOption({ title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { fontSize: 13, color: '#999' } } })
        } else {
          const months = trendData.map(d => d.month.slice(5))
          area.setOption({
            tooltip: { trigger: 'axis', textStyle: { fontSize: 12 }, borderRadius: 6, borderColor: '#e5e7eb' },
            legend: { bottom: 0, textStyle: { fontSize: 11, color: '#6b7280' }, icon: 'line', itemWidth: 12 },
            grid: { top: 10, right: 20, bottom: 36, left: 40 },
            xAxis: { type: 'category', data: months, boundaryGap: false, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false }, axisLabel: { fontSize: 11, color: '#6b7280' } },
            yAxis: { type: 'value', min: 0, max: 100, axisLabel: { fontSize: 11, color: '#6b7280', formatter: '{value}%' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } },
            series: [
              { name: 'RI 覆盖率', type: 'line', data: trendData.map(d => d.riCoverage), smooth: true, lineStyle: { width: 2, color: '#3d9e7a' }, itemStyle: { color: '#3d9e7a' }, areaStyle: { color: 'rgba(61,158,122,0.1)' }, symbol: 'circle', symbolSize: 6 },
              { name: 'SP 覆盖率', type: 'line', data: trendData.map(d => d.spCoverage), smooth: true, lineStyle: { width: 2, color: '#7b68b8' }, itemStyle: { color: '#7b68b8' }, areaStyle: { color: 'rgba(123,104,184,0.1)' }, symbol: 'circle', symbolSize: 6 },
            ],
          })
        }
      }
      // Horizontal bar: coverage by payer (filtered by partner/payer selection)
      const hbar = this.initChart('coverageBarChart')
      if (hbar) {
        const data = this.filteredPayerCompare
        if (data.length === 0) {
          hbar.setOption({ title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { fontSize: 13, color: '#999' } } })
        } else {
          hbar.setOption({
            tooltip: { trigger: 'axis', textStyle: { fontSize: 12 }, borderRadius: 6, borderColor: '#e5e7eb' },
            legend: { bottom: 0, textStyle: { fontSize: 11, color: '#6b7280' }, icon: 'rect', itemWidth: 8, itemHeight: 8 },
            grid: { top: 10, right: 20, bottom: 36, left: 100 },
            xAxis: { type: 'value', min: 0, max: 100, axisLabel: { fontSize: 11, color: '#6b7280', formatter: '{value}%' }, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } },
            yAxis: { type: 'category', data: data.map(d => d.name), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 11, color: '#6b7280' } },
            series: [
              { name: 'RI 覆盖率', type: 'bar', data: data.map(d => d.riCoverage), itemStyle: { color: '#3d9e7a', borderRadius: [0, 3, 3, 0] }, barGap: '25%' },
              { name: 'SP 覆盖率', type: 'bar', data: data.map(d => d.spCoverage), itemStyle: { color: '#7b68b8', borderRadius: [0, 3, 3, 0] } },
            ],
          })
        }
      }
    },

    onPartnerChange() { this.payerFilter = 'all' },
    togglePartner(id) { this.expandedPartner = this.expandedPartner === id ? null : id },
    togglePayer(id) { this.expandedPayer = this.expandedPayer === id ? null : id },
    utilColor(v) { return v >= 80 ? '#3d9e7a' : v >= 60 ? '#c4894a' : '#c05c5c' },
    statusLabel(s) { return s === 'active' ? '活跃' : s === 'expiring-soon' ? '即将到期' : '已过期' },
    statusBadgeClass(s) {
      const base = 'rs-status-badge'
      if (s === 'active') return base + ' rs-status-active'
      if (s === 'expiring-soon') return base + ' rs-status-expiring'
      return base + ' rs-status-expired'
    },
    daysLeft(ri) { return Math.ceil((new Date(ri.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) },
    daysLeftText(d) { return d < 0 ? `已过期 ${Math.abs(d)}天` : `${d}天` },
    daysLeftClass(d) {
      const base = 'rs-td-mono rs-td-days'
      if (d < 0) return base + ' rs-days-red'
      if (d < 90) return base + ' rs-days-red'
      if (d < 180) return base + ' rs-days-amber'
      return base + ' rs-days-green'
    },
    optTypeLabel(t) {
      const m = { 'buy-ri': '购买 RI', 'buy-sp': '购买 SP', 'renew-ri': '续期 RI', 'renew-sp': '续期 SP', 'rightsize': '调整规格', 'terminate': '终止/不续期' }
      return m[t] || t
    },
    optTypeClass(t) {
      const m = { 'buy-ri': 'rs-opt-blue', 'buy-sp': 'rs-opt-purple', 'renew-ri': 'rs-opt-emerald', 'renew-sp': 'rs-opt-emerald', 'rightsize': 'rs-opt-amber', 'terminate': 'rs-opt-red' }
      return m[t] || 'rs-opt-blue'
    },
    optConfLabel(c) { return c === 'high' ? '高' : c === 'medium' ? '中' : '低' },
    optConfClass(c) {
      if (c === 'high') return 'rs-conf-high'
      if (c === 'medium') return 'rs-conf-medium'
      return 'rs-conf-low'
    },
  },
}
</script>

<style scoped>
/* ---- Page ---- */
.rs-page {
  background: #f0f2f5 !important;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'PingFang SC', 'Segoe UI', Roboto, sans-serif;
  color: #1b1b1f;
  letter-spacing: -0.011em;
}
.rs-content { padding: 20px; display: flex; flex-direction: column; gap: 16px; }

/* ---- KPI Row ---- */
.rs-kpi-row { display: grid; grid-template-columns: repeat(7, 1fr); gap: 12px; }
.rs-kpi-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 10px 12px; }
.rs-kpi-header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.rs-kpi-icon { display: flex; align-items: center; }
.rs-kpi-label { font-size: 11px; color: #71717a; }
.rs-kpi-value { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; color: #1b1b1f; }

/* ---- Tabs ---- */
.rs-tabs >>> .el-tabs__header { margin-bottom: 0; }
.rs-tabs >>> .el-tabs__nav-wrap::after { display: none; }
.rs-tabs >>> .el-tabs__item { font-size: 12px; height: 32px; line-height: 32px; padding: 0 14px; color: #71717a; }
.rs-tabs >>> .el-tabs__item.is-active { color: #1b1b1f; font-weight: 600; }
.rs-tabs >>> .el-tabs__active-bar { background-color: #1b1b1f; }
.rs-tab-badge { display: inline-block; font-size: 10px; padding: 1px 6px; border-radius: 9999px; margin-left: 4px; font-weight: 500; }
.rs-badge-orange { background: #fff7ed; color: #c2410c; }
.rs-badge-amber { background: #fffbeb; color: #b45309; }

/* ---- Filters ---- */
.rs-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.rs-search-wrap { position: relative; }
.rs-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #71717a; }
.rs-search-input {
  padding: 6px 12px 6px 32px; font-size: 12px; border: 1px solid #e4e4e7; border-radius: 6px;
  background: #fff; width: 176px; outline: none; color: #1b1b1f;
}
.rs-search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
.rs-filter-select { width: 160px; }
.rs-filter-month { width: 116px; }
.rs-filter-payer { width: 176px; }
.rs-filter-type { width: 112px; }
.rs-filter-status { width: 128px; }
.rs-filters >>> .el-input__inner { font-size: 12px; height: 32px; }
.rs-filters >>> .el-input__wrapper { height: 32px; }

/* ---- Tab body ---- */
.rs-tab-body { display: flex; flex-direction: column; gap: 16px; }

/* ---- Cards ---- */
.rs-chart-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 16px; }
.rs-card-title { font-size: 13px; font-weight: 600; color: #1b1b1f; margin: 0; }
.rs-card-subtitle { font-size: 12px; color: #71717a; margin: 4px 0 12px; }
.rs-chart { width: 100%; }

/* ---- Overview grid ---- */
.rs-overview-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; }
.rs-chart-main { grid-column: 1; }
.rs-chart-side { display: flex; flex-direction: column; gap: 12px; }
.rs-pie-card { padding: 16px; }
.rs-pie-row { display: flex; align-items: center; gap: 12px; }
.rs-pie-chart { width: 80px; height: 80px; flex-shrink: 0; }
.rs-pie-legend { display: flex; flex-direction: column; gap: 6px; }
.rs-legend-item { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.rs-legend-dot { width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; }
.rs-legend-label { color: #71717a; }
.rs-legend-value { font-weight: 500; font-variant-numeric: tabular-nums; color: #1b1b1f; }

/* ---- Table ---- */
.rs-table-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; overflow: hidden; }
.rs-table-header { padding: 12px 20px; border-bottom: 1px solid #e4e4e7; }
.rs-table-header-between { display: flex; align-items: center; justify-content: space-between; }
.rs-table-wrap { overflow-x: auto; }
.rs-empty-state { padding: 48px 20px; text-align: center; color: #71717a; font-size: 13px; }
.rs-table { width: 100%; font-size: 12px; border-collapse: collapse; }
.rs-table thead tr { border-bottom: 1px solid #e4e4e7; background: rgba(244,244,245,0.3); }
.rs-table th { text-align: left; padding: 10px 12px; font-weight: 500; color: #71717a; white-space: nowrap; }
.rs-table tbody tr { border-bottom: 1px solid #f4f4f5; transition: background 0.15s; }
.rs-table tbody tr:last-child { border-bottom: none; }
.rs-table tbody tr:hover { background: rgba(244,244,245,0.2); }
.rs-table td { padding: 10px 12px; }
.rs-td-id { font-variant-numeric: tabular-nums; color: #2563eb; font-weight: 500; }
.rs-td-mono { font-variant-numeric: tabular-nums; color: #1b1b1f; }
.rs-td-muted { color: #71717a; }
.rs-td-green { color: #059669; font-weight: 500; }
.rs-row-expired { background: rgba(254,226,226,0.3); }

/* ---- SP×link 展开行 ---- */
.rs-row-sp-expandable { cursor: pointer; }
.rs-row-sp-expandable:hover { background: rgba(245,243,255,0.5) !important; }
.rs-sp-caret { display: inline-block; width: 12px; color: #8b5cf6; font-size: 11px; margin-right: 2px; }
.rs-sp-detail-row > td { padding: 0 !important; background: #faf9ff; }
.rs-sp-detail { padding: 10px 16px 14px 34px; }
.rs-sp-detail-title { font-size: 12px; font-weight: 600; color: #6d28d9; margin-bottom: 8px; }
.rs-sp-detail-msg { font-size: 12px; color: #71717a; padding: 6px 0; }
.rs-sp-detail-empty { color: #a1a1aa; }
.rs-sp-detail-table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; border: 1px solid #eee6ff; border-radius: 6px; overflow: hidden; }
.rs-sp-detail-table th { text-align: left; padding: 7px 12px; background: #f5f3ff; color: #6d28d9; font-weight: 500; border-bottom: 1px solid #eee6ff; }
.rs-sp-detail-table td { padding: 7px 12px; border-bottom: 1px solid #f6f4fc; }
.rs-sp-detail-table tbody tr:last-child td { border-bottom: none; }
.rs-sp-detail-table tbody tr:hover { background: #faf9ff; }
.rs-sp-detail-svc { max-width: 260px; white-space: normal; line-height: 1.5; }
.rs-sp-detail-foot { font-weight: 600; color: #1b1b1f; border-top: 1px solid #eee6ff; background: #faf9ff; }

/* ---- Type badge ---- */
.rs-type-badge { font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 4px; }
.rs-badge-ri { background: #eff6ff; color: #1d4ed8; }
.rs-badge-sp { background: #f5f3ff; color: #6d28d9; }

/* ---- Status badge ---- */
.rs-status-badge { font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 4px; border: 1px solid; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.rs-status-active { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.rs-status-expiring { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.rs-status-expired { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }

/* ---- Util bar ---- */
.rs-util-bar { display: flex; align-items: center; gap: 8px; }
.rs-util-track { width: 64px; height: 6px; background: #f4f4f5; border-radius: 9999px; overflow: hidden; }
.rs-util-fill { height: 100%; border-radius: 9999px; transition: width 0.3s; }
.rs-util-text { font-size: 12px; font-variant-numeric: tabular-nums; color: #1b1b1f; width: 32px; }

/* ---- Days left ---- */
.rs-td-days { font-weight: 500; }
.rs-days-red { color: #dc2626; }
.rs-days-amber { color: #d97706; }
.rs-days-green { color: #16a34a; }

/* ---- Action buttons (expiration tab) ---- */
.rs-action-btn { font-size: 10px; padding: 4px 8px; border-radius: 4px; cursor: pointer; transition: background 0.15s; }
.rs-action-renew { background: #eff6ff; color: #1d4ed8; }
.rs-action-renew:hover { background: #dbeafe; }
.rs-action-evaluate { background: #fffbeb; color: #b45309; }
.rs-action-evaluate:hover { background: #fef3c7; }
.rs-action-expired { background: #fef2f2; color: #b91c1c; }
.rs-action-expired:hover { background: #fee2e2; }

/* ---- Hierarchy ---- */
.rs-hierarchy { }
.rs-h-partner { border-bottom: 1px solid #e4e4e7; }
.rs-h-partner:last-child { border-bottom: none; }
.rs-h-partner-btn {
  width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 20px;
  background: none; border: none; cursor: pointer; text-align: left; transition: background 0.15s;
}
.rs-h-partner-btn:hover { background: rgba(244,244,245,0.2); }
.rs-h-chevron { color: #71717a; transition: transform 0.2s; flex-shrink: 0; }
.rs-h-chevron.expanded { transform: rotate(90deg); }
.rs-h-chevron-sm { width: 12px; height: 12px; }
.rs-h-partner-name { font-size: 13px; font-weight: 600; color: #1b1b1f; flex: 1; }
.rs-h-meta { font-size: 12px; color: #71717a; }
.rs-h-util { font-size: 12px; font-weight: 500; color: #1b1b1f; width: 80px; text-align: right; }
.rs-h-savings { font-size: 12px; font-weight: 500; color: #059669; width: 100px; text-align: right; }

.rs-h-payer-wrap { border-top: 1px solid #e4e4e7; background: rgba(244,244,245,0.05); }
.rs-h-payer-btn {
  width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px 20px 10px 40px;
  background: none; border: none; cursor: pointer; text-align: left; transition: background 0.15s;
}
.rs-h-payer-btn:hover { background: rgba(244,244,245,0.2); }
.rs-h-payer-name { font-size: 12px; font-weight: 500; color: #1b1b1f; flex: 1; }
.rs-h-links { padding: 0 20px 8px 64px; }
.rs-h-link-row {
  display: flex; align-items: center; gap: 12px; padding: 6px 0; font-size: 12px;
  border-top: 1px dashed rgba(228,228,231,0.5);
}
.rs-h-link-id { font-variant-numeric: tabular-nums; color: #2563eb; width: 64px; }
.rs-h-link-type { font-variant-numeric: tabular-nums; color: #1b1b1f; }
.rs-h-link-name { color: #71717a; }
.rs-h-link-util { margin-left: auto; font-variant-numeric: tabular-nums; color: #1b1b1f; }
.rs-h-link-savings { color: #059669; font-variant-numeric: tabular-nums; width: 100px; text-align: right; }

/* ---- Coverage grid ---- */
.rs-coverage-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

/* ---- Optimization tab ---- */
.rs-opt-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.rs-opt-kpi-card { background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 12px 16px; }
.rs-opt-kpi-label { font-size: 12px; color: #71717a; margin-bottom: 4px; }
.rs-opt-kpi-value { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; color: #1b1b1f; }
.rs-color-emerald { color: #059669; }
.rs-color-blue { color: #2563eb; }
.rs-opt-filters { display: flex; align-items: center; gap: 8px; }
.rs-opt-list { display: flex; flex-direction: column; gap: 8px; }
.rs-opt-card {
  background: #fff; border: 1px solid #e4e4e7; border-radius: 8px; padding: 16px;
  display: flex; align-items: flex-start; gap: 16px; transition: box-shadow 0.15s;
}
.rs-opt-card:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
.rs-opt-icon-wrap { padding: 8px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.rs-opt-blue { background: #eff6ff; color: #1d4ed8; }
.rs-opt-purple { background: #f5f3ff; color: #6d28d9; }
.rs-opt-emerald { background: #ecfdf5; color: #047857; }
.rs-opt-amber { background: #fffbeb; color: #b45309; }
.rs-opt-red { background: #fef2f2; color: #b91c1c; }
.rs-opt-body { flex: 1; min-width: 0; }
.rs-opt-badges { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.rs-opt-type-badge { font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 4px; }
.rs-opt-conf-badge { font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 4px; }
.rs-conf-high { background: #ecfdf5; color: #059669; }
.rs-conf-medium { background: #fffbeb; color: #d97706; }
.rs-conf-low { background: #f4f4f5; color: #52525b; }
.rs-opt-title { font-size: 13px; font-weight: 600; color: #1b1b1f; margin: 0 0 2px; }
.rs-opt-desc { font-size: 12px; color: #71717a; margin: 0; }
.rs-opt-savings { text-align: right; flex-shrink: 0; }
.rs-opt-savings-value { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; color: #059669; }
.rs-opt-savings-label { font-size: 10px; color: #71717a; }
.rs-opt-arrow { color: #71717a; flex-shrink: 0; margin-top: 4px; }
.rs-pagination { display: flex; justify-content: flex-end; padding: 12px 16px 4px; }
.rs-empty-cell { padding: 28px 16px; text-align: center; color: #71717a; font-size: 13px; }
</style>
